import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { finalize } from 'rxjs';
import { Auth, UserService } from 'src/app/service';

import { User } from '../schemas';
import { UserForm, UserFormData } from './user-form';

export interface HumanizedUser extends User {
  HumanRole: string;
  HumanState: string;
  HumanTeam: string;
}

@Component({
  selector: 'lcm-user-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatSortModule,
    MatSort,
    MatPaginator,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatDialogModule,
  ],
  template: `
    <table mat-table [dataSource]="source()" [trackBy]="trackBy" matSort>
      <ng-container matColumnDef="Email">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Email</th>
        <td mat-cell *matCellDef="let user">{{ user.Email }}</td>
      </ng-container>

      <ng-container matColumnDef="FullName">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Full Name</th>
        <td mat-cell *matCellDef="let user">{{ user.FullName }}</td>
      </ng-container>

      <ng-container matColumnDef="HumanRole">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Role</th>
        <td mat-cell *matCellDef="let user">{{ user.HumanRole }}</td>
      </ng-container>

      <ng-container matColumnDef="HumanState">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>State</th>
        <td mat-cell *matCellDef="let user">{{ user.HumanState }}</td>
      </ng-container>

      <ng-container matColumnDef="HumanTeam">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Team</th>
        <td mat-cell *matCellDef="let user">{{ user.HumanTeam }}</td>
      </ng-container>

      <ng-container matColumnDef="Capacity">
        <th mat-header-cell mat-sort-header *matHeaderCellDef>Capacity</th>
        <td mat-cell *matCellDef="let user">{{ user.Capacity }}</td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let user">
          <div class="cell-actions justify-end">
            @if (auth.isSuperAdmin() && user.State === 'inactive') {
              <button matMiniFab (click)="remove(user)" [disabled]="pending() || isRemoving()">
                <mat-icon>delete</mat-icon>
              </button>
            }

            @if (auth.isAdmin()) {
              <button matMiniFab (click)="showForm(user)" [disabled]="pending() || isRemoving()">
                <mat-icon>edit</mat-icon>
              </button>
            }
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="columnsToShow()"></tr>
      <tr mat-row *matRowDef="let user; columns: columnsToShow()"></tr>
    </table>
    <mat-paginator [showFirstLastButtons]="true" [pageSize]="10"></mat-paginator>
  `,
  styles: ``,
})
export class UserTable {
  auth = inject(Auth);
  dialog = inject(MatDialog);
  userService = inject(UserService);

  edited = output<User>();
  removed = output<User>();

  users = input.required<HumanizedUser[]>();
  pending = input(false);

  isRemoving = signal(false);

  source = computed(() => {
    const source = new MatTableDataSource(this.users());
    source.paginator = this.paginator();
    source.sort = this.sort();
    return source;
  });

  trackBy(idx: number, row: User) {
    return row.Email;
  }

  paginator = viewChild.required(MatPaginator);
  sort = viewChild.required(MatSort);

  columnsToShow = signal(['Email', 'FullName', 'HumanRole', 'HumanState', 'HumanTeam', 'Capacity', 'actions']);

  showForm(user: HumanizedUser) {
    this.dialog
      .open(UserForm, {
        data: { user: user, title: 'Update user account' } as Required<UserFormData>,
      })
      .afterClosed()
      .subscribe({ next: (result) => result && this.edited.emit(result) });
  }

  remove(user: User) {
    this.isRemoving.set(true);
    this.userService
      .delete(user)
      .pipe(finalize(() => this.isRemoving.set(false)))
      .subscribe({ next: (data: User) => this.removed.emit(data) });
  }
}
